import { useState, useCallback, useEffect } from "react"
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner'; 
import { CheckCircle, DollarSign } from 'lucide-react'
import ChangePassword from "../../cors/ChangePassword";
import SelectSallers from "../../cors/SelectSallers";
import { useAuth } from "../../context/AuthProvider";

export default function UpdateConfirmatorForm({ userId, currentUserData, fetchData, sellersData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSellers, setSelectedSellers] = useState([]);
  const { token, role, roles } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    phone: "",
    email: "",
    rib: "",
    workStatus: false,
    priceConfirm: "",
    priceWork: "",
    selectedPriceOption: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)
  const [serverError, setServerError] = useState("")

  useEffect(() => {
    if (currentUserData?.info) {
      const priceOption = currentUserData.info.work_status === 1 ? "work" : "confirm"
      setFormData({
        name: currentUserData.info.name || "",
        gender: currentUserData.info.gender || "",
        phone: String(currentUserData.info.phone || ""), 
        email: currentUserData.email || "",
        rib: String(currentUserData.info.rib || ""),
        workStatus: currentUserData.info.work_status === 1,
        priceConfirm: currentUserData.info.price_confirm || "",
        priceWork: currentUserData.info.price_work || "",
        selectedPriceOption: priceOption,
      })
      
      // Initialize selected sellers from currentUserData
      if (currentUserData.info.sellers && currentUserData.info.sellers.length > 0) {
        setSelectedSellers(currentUserData.info.sellers.map(seller => seller.id));
      }
    }
  }, [currentUserData])

  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target

      setFormData((prevFormData) => {
        const updatedFormData = { ...prevFormData }

        if (name === "priceOption") {
          updatedFormData.selectedPriceOption = value
          if (value === "confirm") {
            updatedFormData.priceWork = ""
            updatedFormData.workStatus = false
          } else if (value === "work") {
            updatedFormData.priceConfirm = ""
            updatedFormData.workStatus = true
          }
        } else if (type === "number") {
          const newValue = value === "" ? "" : Number(value)
          updatedFormData[name] = newValue

          if (name === "priceConfirm" && newValue !== "") {
            updatedFormData.selectedPriceOption = "confirm"
            updatedFormData.workStatus = false
          } else if (name === "priceWork" && newValue !== "") {
            updatedFormData.selectedPriceOption = "work"
            updatedFormData.workStatus = true
          }
        } else if (type === "checkbox") {
          updatedFormData[name] = checked
        } else {
          updatedFormData[name] = value
        }

        return updatedFormData
      })

      if (errors[name]) {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }))
      }
      setServerError("")
      setSuccess(false)
    },
    [errors],
  )

  const validateEmail = (email) => {
    return /^[^\s.]+.[^\s.]+\.[^\s.]+$/.test(email)
  }

  const validateForm = useCallback(() => {
    const newErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = "L'adresse e-mail est obligatoire."
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "L'adresse e-mail doit être valide."
    }

    if (!formData.name.trim()) {
      newErrors.name = "Le nom est obligatoire."
    }

    if (!formData.gender) {
      newErrors.gender = "Le sexe est obligatoire."
    }

   if (!formData.phone) {
      newErrors.phone = "Le numéro de téléphone est obligatoire."
    } else if (!/^\d+$/.test(formData.phone)) { // No need for trim() now
      newErrors.phone = "Le numéro de téléphone doit être un nombre."
    }

    if (!formData.rib) {
      newErrors.rib = "Le RIB est obligatoire."
    } else if (!/^\d+$/.test(formData.rib)) { // No need for trim() now
      newErrors.rib = "Le RIB doit être un nombre."
    }

    if (!formData.selectedPriceOption) {
      newErrors.priceConfirm = "Veuillez sélectionner un type de prix."
      newErrors.priceWork = "Veuillez sélectionner un type de prix."
    } else if (formData.selectedPriceOption === "confirm") {
      if (formData.priceConfirm === "" || formData.priceConfirm === null) {
        newErrors.priceConfirm = "Le tarif de confirmation est obligatoire."
      } else if (typeof formData.priceConfirm !== "number" || formData.priceConfirm < 0) {
        newErrors.priceConfirm = "Le tarif de confirmation doit être un nombre positif."
      }
    } else if (formData.selectedPriceOption === "work") {
      if (formData.priceWork === "" || formData.priceWork === null) {
        newErrors.priceWork = "Le tarif de travail est obligatoire."
      } else if (typeof formData.priceWork !== "number" || formData.priceWork < 0) {
        newErrors.priceWork = "Le tarif de travail doit être un nombre positif."
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  const handleSubmit = async (e) => {
    e.preventDefault()

    setServerError("")
    setSuccess(false)

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const payload = {
        name: formData.name,
        gender: formData.gender,
        phone: formData.phone,
        email: formData.email,
        rib: formData.rib,
        work_status: formData.workStatus ? 1 : 0, // Convert to 1/0 for API
        sellers: selectedSellers, // Add selected sellers to payload
      };

      if (
        formData.selectedPriceOption === "confirm" &&
        formData.priceConfirm !== "" &&
        formData.priceConfirm !== null
      ) {
        payload.price_confirm = formData.priceConfirm
      } else if (formData.selectedPriceOption === "work" && formData.priceWork !== "" && formData.priceWork !== null) {
        payload.price_work = formData.priceWork
      }

      const response = await fetch(`${BASE_URL}/api/confirmators/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,  
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          const newErrors = {}
          for (const key in data.errors) {
            let clientKey = key
            if (key === "price_confirm") clientKey = "priceConfirm"
            if (key === "price_work") clientKey = "priceWork"
            if (key === "sellers") clientKey = "sellers"
            newErrors[clientKey] = data.errors[key][0]
          }
          setErrors(newErrors)
          setServerError("Veuillez corriger les erreurs dans le formulaire.")
        } else {
          toast.error("Erreur de mise à jour du confirmateur!")
          setServerError(data.message || "Une erreur inattendue est survenue.")
        }
        return
      }

      setSuccess(true)
      setErrors({})
      setServerError("")
      toast.success("Confirmateur mis à jour avec succès !")
      setIsEditing(false)
      fetchData()

    
    } catch (error) {
      console.error("Error updating confirmator:", error)
      setServerError("Échec de la connexion au serveur. Veuillez réessayer.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    if (currentUserData?.info) {
      const priceOption = currentUserData.info.work_status === 1 ? "work" : "confirm"
      setFormData({
        name: currentUserData.info.name || "",
        gender: currentUserData.info.gender || "",
        phone: String(currentUserData.info.phone || ""), 
        email: currentUserData.email || "",
        rib: String(currentUserData.info.rib || ""),
        workStatus: currentUserData.info.work_status === 1,
        priceConfirm: currentUserData.info.price_confirm || "",
        priceWork: currentUserData.info.price_work || "",
        selectedPriceOption: priceOption,
      })
    }
    setIsEditing(false)
    setErrors({})
    setServerError("")
    setSuccess(false)
  }

  // Prepare seller options for the Select component
  const sellerOptions = sellersData.map(seller => ({
    value: seller.id,
    label: `${seller.name} (${seller.name_store})`
  }));

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  }

  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }



  return (
    <div className="max-w-5xl mx-auto min-h-screen px-4">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 md:w-32 md:h-32 bg-white/20 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 md:w-16 md:h-16 text-white" />
                </div>
                <div className="text-white">
                  <h1 className="text-xl md:text-2xl font-bold">Profil Confirmateur</h1>
                  <p className="text-emerald-100 text-xs">Gestion des confirmateurs</p>
                  <p className="text-[11px] text-emerald-100 mt-1">
                    {currentUserData?.info.work_status ? "Prix par travail" : "Prix par confirmation"}
                  </p>
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                  <div className="text-xl font-bold">Confirm</div>
                  <div className="text-xs text-emerald-500">Rôle spécialisé</div>
                </div>
              </div>
            </div>
          </div>
          <div className="px-6 py-6">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatMoney(currentUserData.info.total_profite)} MAD</div>
                <div className="text-sm text-gray-600">Total Profite</div>
                <div className="text-xs text-green-600 mt-1">↗ +23% vs last month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatCount(currentUserData.info.total_orders)}</div>
                <div className="text-sm text-gray-600">Total Commandes</div>
                <div className="text-xs text-green-600 mt-1">↗ +15% vs last month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{currentUserData.info.confirmed_percentage}%</div>
                <div className="text-sm text-gray-600">Pourcentage confirmation</div>
                <div className="text-xs text-green-600 mt-1">↗ +2.1% vs last month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatCount(currentUserData.info.total_confirmed_orders)}</div>
                <div className="text-sm text-gray-600">Total confirmé</div>
                <div className="text-xs text-green-600 mt-1">↗ +18% vs last month</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatCount(currentUserData.info.total_sellers)}</div>
                <div className="text-sm text-gray-600">Total Vendeurs</div>
                <div className="text-xs text-green-600 mt-1">↗ +18% vs last month</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

          {serverError && (
            <div className="bg-red-500 text-white p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-lg font-semibold">Erreur !</span>
              </div>
            </div>
          )}

          <div className="p-8 max-xl:px-5">
            {
              (role === 1 || (role === 2 && roles.edit_confirmator === 1)) &&
                <div className="flex justify-end space-x-3">
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)] cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span className="text-sm max-xl:text-xs">Modifier le Profil</span>
                    </button>
                  ) : (
                    <div className="flex space-x-3">
                      <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center space-x-2 ${
                          isLoading ? 'opacity-70' : ''
                        }`}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span className="max-xl:text-xs">Mise à jour...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm max-xl:text-xs">Enregistrer</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleCancel}
                        className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span className="text-sm max-xl:text-xs">Annuler</span>
                      </button>
                    </div>
                  )}
                </div>
            }

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="p-8 max-xl:px-2">
                <div className="flex items-center mb-6">
                  <div className="bg-[var(--color-primary-50)] p-3 rounded-lg mr-4">
                    <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Informations personnelles</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-2">
                      Adresse e-mail *
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="utilisateur.example.com"
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.email
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.email || "Non spécifié"}
                      </p>
                    )}
                    {errors.email && <p className="mt-2 text-red-600 text-xs">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-600 mb-2">
                      Nom complet *
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Entrez le nom complet..."
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.name
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.info.name || "Non spécifié"}
                      </p>
                    )}
                    {errors.name && <p className="mt-2 text-red-600 text-xs">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="gender" className="block text-sm font-medium text-gray-600 mb-2">
                      Sexe *
                    </label>
                    {isEditing ? (
                      <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.gender
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      >
                        <option value="">Sélectionnez le sexe</option>
                        <option value="Male">Homme</option>
                        <option value="Female">Femme</option>
                      </select>
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.info.gender === "Male" ? "Homme" : currentUserData?.info.gender === "Female" ? "Femme" : "Non spécifié"}
                      </p>
                    )}
                    {errors.gender && <p className="mt-2 text-red-600 text-xs">{errors.gender}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-600 mb-2">
                      Numéro de téléphone *
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="0612345678"
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          errors.phone
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                        {currentUserData?.info.phone || "Non spécifié"}
                      </p>
                    )}
                    {errors.phone && <p className="mt-2 text-red-600 text-xs">{errors.phone}</p>}
                  </div>

                  <div>
                    <label htmlFor="rib" className="block text-sm font-medium text-gray-600 mb-2">
                      Numéro RIB *
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        id="rib"
                        name="rib"
                        value={formData.rib}
                        onChange={handleInputChange}
                        placeholder="12345678901234567890"
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white font-mono ${
                          errors.rib
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                      />
                    ) : (
                      <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl font-mono">
                        {currentUserData?.info.rib || "Non spécifié"}
                      </p>
                    )}
                    {errors.rib && <p className="mt-2 text-red-600 text-xs">{errors.rib}</p>}
                  </div>
                </div>
              </div>

              <div className="p-8 max-xl:px-2">
                <div className="flex items-center mb-6">
                  <div className="bg-[var(--color-primary-500)]/15 p-3 rounded-lg mr-4">
                    <DollarSign  className="w-6 h-6 text-[var(--color-primary-500)]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">Configuration des prix</h2>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between max-xl:grid max-xl:space-y-2 max-xl:justify-center  p-6 bg-gray-50 rounded-2xl border-2 border-gray-200">
                    <div className="flex items-center space-x-4">
                      {isEditing ? (
                        <>
                          <input
                            type="radio"
                            id="priceOptionConfirm"
                            name="priceOption"
                            value="confirm"
                            checked={formData.selectedPriceOption === "confirm"}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-[var(--color-primary-500)] border-gray-300 focus:ring-[var(--color-primary-500)]"
                          />
                          <label htmlFor="priceOptionConfirm" className="text-xs font-medium text-gray-900">
                            Prix par confirmation
                          </label>
                        </>
                      ) : (
                        <span className="text-xs font-semibold text-gray-900">
                          Prix par confirmation {formData.selectedPriceOption === "confirm" && "(Actif)"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">MAD</span>
                      {isEditing ? (
                        <input
                          type="number"
                          id="priceConfirm"
                          name="priceConfirm"
                          value={formData.priceConfirm}
                          onChange={handleInputChange}
                          placeholder="25.00"
                          step="0.01"
                          min="0"
                          disabled={formData.selectedPriceOption !== "confirm"}
                          className={`w-32 py-2 px-3 text-sm border-2 rounded-xl transition-all duration-300 ${
                            formData.selectedPriceOption === "confirm"
                              ? "border-[var(--color-primary-500)] bg-white focus:ring-4 focus:ring-[var(--color-primary-100)]"
                              : "border-gray-300 bg-gray-100"
                          } focus:outline-none`}
                        />
                      ) : (
                        currentUserData?.info.price_confirm > 0&&
                        <p className="text-gray-800 text-sm bg-gray-50 px-4 py-2 rounded-xl">
                          {currentUserData?.info.price_confirm || "Non spécifié"}
                        </p>
                      )}
                    </div>
                  </div>
                  {errors.priceConfirm && <p className="text-red-600 text-xs">{errors.priceConfirm}</p>}
                  
                  <div className="flex items-center justify-between max-xl:grid max-xl:space-y-2 max-xl:justify-center p-6 bg-gray-50 rounded-2xl border-2 border-gray-200">
                    <div className="flex items-center space-x-4">
                      {isEditing ? (
                        <>
                          <input
                            type="radio"
                            id="priceOptionWork"
                            name="priceOption"
                            value="work"
                            checked={formData.selectedPriceOption === "work"}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-[var(--color-primary-500)] border-gray-300 focus:ring-[var(--color-primary-500)]"
                          />
                          <label htmlFor="priceOptionWork" className="text-xs font-semibold text-gray-900">
                            Prix par travail
                          </label>
                        </>
                      ) : (
                        <span className="text-xs font-medium text-gray-900">Prix par travail</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">MAD</span>
                      {isEditing ? (
                        <input
                          type="number"
                          id="priceWork"
                          name="priceWork"
                          value={formData.priceWork}
                          onChange={handleInputChange}
                          placeholder="500.00"
                          step="0.01"
                          min="0"
                          disabled={formData.selectedPriceOption !== "work"}
                          className={`w-32 py-2 px-3 text-sm border-2 rounded-xl transition-all duration-300 ${
                            formData.selectedPriceOption === "work"
                              ? "border-[var(--color-primary-500)] bg-white focus:ring-4 focus:ring-[var(--color-primary-100)]"
                              : "border-gray-300 bg-gray-100"
                          } focus:outline-none`}
                        />
                      ) : (
                        <p className="text-gray-800 text-sm bg-gray-50 px-4 py-2 rounded-xl">
                          {currentUserData?.info.price_work || "Non spécifié"}
                        </p>
                      )}
                    </div>
                  </div>
                  {errors.priceWork && <p className="text-red-600 text-xs">{errors.priceWork}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SelectSallers 
          sellerOptions={sellerOptions} 
          selectedSellers={selectedSellers}
          setSelectedSellers={setSelectedSellers}
          sellersData={sellersData}
          currentUserData={currentUserData}  
          />
       {
        (role === 1 || (role === 2 && roles.change_confirmator_password === 1)) &&
          <ChangePassword userId={userId} />  
       }
      </div>
    </div>
  )
}